/**
 * ProvinceOverview — the province at a glance: members, attendance, giving,
 * a 12-month trend, the assemblies ranked, and what needs a visit.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, TrendingUp, TrendingDown, Minus, AlertTriangle, Users, HandCoins, CalendarCheck,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchJson = async (path) => {
  const r = await fetch(`${API_URL}${path}`, { headers: authHeaders() });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Request failed');
  return r.json();
};

const money = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const Delta = ({ now, before, testId }) => {
  const diff = Number(now || 0) - Number(before || 0);
  const pct = before ? Math.round((diff / Number(before)) * 100) : null;
  const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  const tone = diff > 0 ? 'text-emerald-700' : diff < 0 ? 'text-rose-600' : 'text-slate-400';
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${tone}`} data-testid={testId}>
      <Icon className="h-3 w-3" />
      {diff === 0
        ? 'level with last month'
        : `${diff > 0 ? '+' : ''}${money(diff)}${pct !== null ? ` (${pct}%)` : ''} on last month`}
    </span>
  );
};

const Stat = ({ icon: Icon, label, value, children, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 flex items-center gap-1.5">
      <Icon className="h-3 w-3" /> {label}
    </p>
    <p className="text-2xl font-semibold text-slate-900 mt-1">{value}</p>
    {children}
  </div>
);

export const ProvinceOverview = ({ unitId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['province-dashboard', unitId],
    queryFn: () => fetchJson(`/api/province/${unitId}/dashboard`),
    enabled: !!unitId,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="py-16 text-center text-slate-400" data-testid="province-overview-loading">
        <Loader2 className="h-5 w-5 animate-spin inline" />
      </div>
    );
  }
  if (error) {
    return <p className="py-16 text-center text-sm text-rose-600" data-testid="province-overview-error">{error.message}</p>;
  }

  const t = data.totals;
  // Each series gets its own scale — giving in currency would otherwise dwarf
  // the attendance bars into nothing.
  const peakAttendance = Math.max(1, ...data.trend.map((m) => m.attendance));
  const peakGiving = Math.max(1, ...data.trend.map((m) => m.giving));

  return (
    <div className="space-y-5" data-testid="province-overview">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Users} label="Members" value={t.members.toLocaleString()} testId="province-stat-members">
          <Delta now={t.members_joined_this_month} before={t.members_joined_last_month} testId="province-members-delta" />
        </Stat>
        <Stat icon={CalendarCheck} label="Attendance this month" value={t.attendance_this_month.toLocaleString()} testId="province-stat-attendance">
          <Delta now={t.attendance_this_month} before={t.attendance_last_month} testId="province-attendance-delta" />
        </Stat>
        <Stat icon={HandCoins} label="Giving this month" value={money(t.giving_this_month)} testId="province-stat-giving">
          <Delta now={t.giving_this_month} before={t.giving_last_month} testId="province-giving-delta" />
        </Stat>
        <Stat icon={Users} label="Assemblies" value={t.assemblies} testId="province-stat-assemblies">
          <span className="text-xs text-slate-400">{t.units} units in the branch</span>
        </Stat>
      </div>

      {!data.viewer.sees_giving_detail && (
        <p className="text-[11px] text-slate-400" data-testid="province-giving-note">
          Giving is shown as totals and trends only — individual gifts and donor names stay with
          the assembly and its finance officers.
        </p>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Last 12 months</CardTitle>
          <CardDescription>Attendance in slate, giving in amber.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-1.5 h-40" data-testid="province-trend">
            {data.trend.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1 group">
                <div className="w-full flex items-end justify-center gap-0.5 h-32">
                  <div className="w-1/2 bg-slate-700 rounded-t transition-all group-hover:bg-slate-900"
                       style={{ height: `${Math.max(2, (m.attendance / peakAttendance) * 100)}%` }}
                       title={`${m.attendance} attended`} />
                  <div className="w-1/2 bg-amber-400 rounded-t transition-all group-hover:bg-amber-500"
                       style={{ height: `${Math.max(2, (m.giving / peakGiving) * 100)}%` }}
                       title={`${money(m.giving)} given`} />
                </div>
                <span className="text-[9px] text-slate-400">{m.month.slice(5)}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {data.attention.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" /> Needs attention ({data.attention.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2" data-testid="province-attention">
            {data.attention.map((a, i) => (
              <Link key={`${a.church_id}-${i}`} to={`/admin/churches/${a.church_id}`}
                    className="text-xs rounded-full bg-white border border-amber-200 px-3 py-1 hover:border-amber-400"
                    data-testid={`province-attention-${i}`}>
                <span className="font-medium text-slate-800">{a.name}</span>
                <span className="text-amber-800"> · {a.issue}</span>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Assemblies ({data.assemblies_table.length})</CardTitle>
          <CardDescription>Ranked by the last 30 days. The bottom three are flagged.</CardDescription>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b">
                <th className="text-left py-2 px-3">Assembly</th>
                <th className="text-right py-2 px-3">Members</th>
                <th className="text-right py-2 px-3">Attendance 30d</th>
                <th className="text-right py-2 px-3">Giving 30d</th>
                <th className="text-left py-2 px-3 pl-4">Last service</th>
              </tr>
            </thead>
            <tbody>
              {data.assemblies_table.map((r) => (
                <tr key={r.id} className={`border-b last:border-0 ${r.needs_attention ? 'bg-amber-50/50' : ''}`}
                    data-testid={`province-assembly-${r.id}`}>
                  <td className="py-2 px-3">
                    <Link to={`/admin/churches/${r.id}`} className="text-slate-800 hover:text-emerald-700 font-medium">
                      {r.name}
                    </Link>
                    {!r.has_pastor && (
                      <Badge variant="outline" className="ml-2 text-[9px] border-rose-200 text-rose-600">no pastor</Badge>
                    )}
                    {r.city && <span className="text-[10px] text-slate-400 ml-1">{r.city}</span>}
                  </td>
                  <td className="py-2 px-3 text-right">{r.members}</td>
                  <td className="py-2 px-3 text-right">{r.attendance_30d}</td>
                  <td className="py-2 px-3 text-right">{money(r.giving_30d)}</td>
                  <td className="py-2 px-3 pl-4 text-xs text-slate-500">{r.last_service || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProvinceOverview;
